import type { ReactNode } from 'react';

import { AppSplash } from './app-splash';
import { Login } from '@/app/routes/login';
import { WorkspaceSettingUp } from './workspace-setting-up';
// WorkspaceRecovery: the old no-workspace stub — retained as a separate
// recovery branch for the rare case of an authenticated user with zero
// workspaces (should not happen post-signup; server auto-provisions one).
import { OnboardingFlow as WorkspaceRecovery } from './onboarding-flow';
// New user onboarding — shown when onboardingCompletedAt is null.
import { OnboardingFlow } from '@/features/onboarding/components/onboarding-flow';
import { useBootState, type BootState } from '@/stores/use-boot-store';
import { useAuthStore } from '@/stores/use-auth-store';

/**
 * The single gate that decides what surface the user sees during app boot.
 * Switches on useBootState() — see @/stores/use-boot-store for the state machine
 * definition and boot-state-machine-design.md for the rationale.
 *
 * Every downstream consumer (Sidebar, PanelLayout, etc.) is rendered as
 * `children` and is therefore structurally inside the 'ready' branch. The
 * full provider stack (SyncSystemProvider, WorkspaceServiceProvider,
 * SnapshotServiceProvider, SessionWrapper) wraps `children` in
 * MainLayout — those providers are now guaranteed to find their
 * preconditions (notably activeWorkspaceId !== null) satisfied because
 * BootGate only mounts `children` when the boot state machine reports ready.
 *
 * Adding a new boot precondition (feature flags, billing, server migration
 * check) is a single-place change: add a variant to BootState, extend
 * useBootState's derivation, add a branch to the switch below. TypeScript's
 * exhaustiveness checker (the `_exhaustive: never` line) catches missed
 * branches at compile time.
 *
 * Onboarding intercept: before any authenticated state reaches the app shell,
 * we check onboardingCompletedAt from the Me payload. undefined = Me fetch
 * still in flight → keep showing splash. null = confirmed not done yet →
 * show the new OnboardingFlow. string = done → render children (app shell).
 * This check is intentionally outside the boot state machine to keep the
 * machine focused on workspace availability.
 */
export function BootGate({ children }: { children: ReactNode }) {
  const state = useBootState();
  const onboardingCompletedAt = useAuthStore((s) => s.onboardingCompletedAt);
  const logout = useAuthStore((s) => s.logout);

  switch (state) {
    case 'splash':
      return <AppSplash />;
    case 'unauthenticated':
      return <Login />;
    case 'workspace-pending':
      return <WorkspaceSettingUp />;
    case 'onboarding-required':
      // Workspace recovery: authenticated user with no workspace (rare recovery path).
      return <WorkspaceRecovery />;
    case 'ready': {
      // onboardingCompletedAt === undefined → Me fetch still in flight; keep
      // showing the splash rather than flashing the onboarding screen.
      if (onboardingCompletedAt === undefined) {
        return <AppSplash />;
      }
      // onboardingCompletedAt === null → confirmed not done → show onboarding.
      // M3 implements the real POST + Me refresh; placeholder until then.
      if (onboardingCompletedAt === null) {
        return (
          <OnboardingFlow
            // No-op: the store flip inside handleFinish (onboardingCompletedAt) re-renders
            // BootGate via Zustand subscription and unmounts the onboarding flow automatically.
            onComplete={() => {}}
            onLogout={() => { void logout(); }}
          />
        );
      }
      return <>{children}</>;
    }
    default: {
      const _exhaustive: never = state;
      void _exhaustive;
      return null;
    }
  }
}

// Re-export for callers that want to ensure their derivation matches the gate.
export type { BootState };
